// KEEP THIS FILE FREE FROM REACT COMPONENTS OR IT WILL BREAK CYPRESS UNIT TESTS.

import { CONFIG_API_ENDPOINT, DATA_API_ENDPOINT } from 'config/constants';
import { differenceInSeconds, format, isAfter, parseISO } from 'date-fns';
import { AuthStateTypes } from 'hooks/useAuth.hook';
import { jwtDecode } from 'jwt-decode';
import { keys } from 'lodash';
import { v4 as uuidv4 } from 'uuid';


export const getAccessToken = (authContext: AuthStateTypes) => {
  if (authContext.accessToken)
    return authContext.accessToken
  if (authContext.jwtToken)
    return authContext.jwtToken;
  return "";
};

export const getIdToken = (authContext: any) => {
  if (!authContext.jwtToken) return null;
  return authContext.jwtToken;
};

export const getUUID = () => {
  return uuidv4();
};



export const tokenHasExpired = (jwtToken: string): boolean => {
  // a function to check if the time now is later than JWT tokens exp time and return boolean.
  if (!jwtToken) return true;
  const decoded: any = jwtDecode(jwtToken);
  const tokenExp = decoded.exp * 1000; // add extra 0s to give correct time value.
  const timeNow = Date.now();
  const tokenHasExpired: any = isAfter(timeNow, tokenExp);

  return tokenHasExpired;
};

export const getJWTTokenExpiryTime = (jwtToken: string): number => {
  const accessTokenDecoded: any = jwtDecode(jwtToken);
  const accessTokenExpTime = accessTokenDecoded.exp * 1000;

  return accessTokenExpTime;
};

export const replaceWordsInDistanceString = (distance: string): string => {
  return distance
    .replace(/hour/i, 'hr')
    .replace(/minute/i, 'min')
    .replace(/second/i, 'sec');
};

export function getLastSeenString(timestamp: string) {
  if (!timestamp) return '––';
  const lastSeenDateObj: any = new Date(timestamp);
  let lastSeenDate = '';

  if (lastSeenDateObj.toString() !== 'Invalid Date') {
    lastSeenDate = `(${format(lastSeenDateObj, 'dd/LL/yyyy - HH:mm')})`;
  }

  return lastSeenDate;
}

export const getValueSafely = (target: any, endOfDataString?: string, showEmptyString?: boolean) => {
  if (!target && showEmptyString) {
    return '';
  }
  if (!target) {
    return '––';
  }
  return `${target}${endOfDataString ? endOfDataString : ''}`;
};

export const DATA_LIMIT = 3

export function humaniseStringCasing(string: string) {
  let i = 0;
  let frags = string.split('_');
  for (i = 0; i < frags.length; i++) {
    frags[i] = frags[i].charAt(0).toUpperCase() + frags[i].slice(1);
  }
  return frags.join(' ');
}

export function getSortedArrayByValue(arr: any[], value: string): any {
  return arr.sort((a: any, b: any) => a[value].toLowerCase().localeCompare(b[value].toLowerCase()));
}


export function getWindowURL() {
  return `${window.location.protocol}//${window.location.hostname}${window.location.port ? `:${window.location.port}` : ''
    }`;
}

export function isProdEnv() {
  return process.env.NODE_ENV === 'production';
}

type BaseApiUrl = {
  type: 'base_api_url';
  url: string;
};

type CompositeApiUrl = {
  type: 'composite_api_url';
  config_api_url: string;
  data_api_url: string;
};

type ApiUrl = BaseApiUrl | CompositeApiUrl;

function parseApiUrl(): ApiUrl {
  const serializedData = process.env.REACT_APP_LOCAL_DEV_BASE_URL as string;
  try {
    const data = JSON.parse(serializedData);
    return {
      type: 'composite_api_url',
      config_api_url: data['config_api_url'],
      data_api_url: data['data_api_url'],
    };
  } catch (e) {
    if (e instanceof SyntaxError) {
      return {
        url: serializedData,
        type: 'base_api_url',
      };
    }
    throw e;
  }
}

export function getConfigApiUrl() {
  if (isProdEnv()) {
    return `${getWindowURL()}${CONFIG_API_ENDPOINT}`;
  }

  const apiUrl = parseApiUrl();
  if (apiUrl.type === 'base_api_url') {
    return `${apiUrl.url}${CONFIG_API_ENDPOINT}`;
  } else {
    return apiUrl.config_api_url;
  }
}

export function getDataApiUrl() {
  if (isProdEnv()) {
    return `${getWindowURL()}${DATA_API_ENDPOINT}`;
  }

  const apiUrl = parseApiUrl();
  if (apiUrl.type === 'base_api_url') {
    return `${apiUrl.url}${DATA_API_ENDPOINT}`;
  } else {
    return apiUrl.data_api_url;
  }
}

export function shouldUseCognito() {
  return process.env.REACT_APP_USE_COGNITO !== 'false';
}

export function programaticallyDownloadFileFromHref(href: string) {
  const link = document.createElement('a');

  link.href = href;
  link.target = "_blank"
  link.setAttribute('download', '');
  document.body.appendChild(link);
  link.click();
  // remove file from DOM.
  document.body.removeChild(link);
}

export function clearWindowURLParams() {
  // function to remove all url params and update history so URL updates.
  const url = new URL(window.location.href);
  const params = new URLSearchParams(url.search);

  for (const [key] of params.entries()) {
    params.delete(key);
  }
  // eslint-disable-next-line no-restricted-globals
  window.history.replaceState({}, '', `${location.pathname}`);
}

export function assetEstimateIsNotFresh(
  estimateTimestamp: string,
  freshPeriodThresholdSeconds: number,
): boolean {
  // simple function that returns true if the assets estimate is greater than the fresh period defined from config.
  const timeNow = new Date();
  const timeDifferenceInSeconds = differenceInSeconds(timeNow, new Date(estimateTimestamp));
  return timeDifferenceInSeconds > freshPeriodThresholdSeconds;
}

export function userEstimateIsNotFresh(
  estimateTimestring: any,
  freshPeriodThresholdSeconds: number,
): boolean {
  // simple function that returns true if the users estimate is older than the fresh period defined from config.
  const timeNow = new Date();
  const estimateDate = parseISO(estimateTimestring);
  const timeDifferenceInSeconds = differenceInSeconds(timeNow, estimateDate);

  return timeDifferenceInSeconds > freshPeriodThresholdSeconds;
}

export function isDeepEqual(x: any, y: any, exceptionKeys: string[] = [], debug: boolean = false): boolean {
  if (x && y && typeof x === 'object' && typeof y === 'object') {

    if (Array.isArray(x) && Array.isArray(y)) {
      if (x.length !== y.length) {
        if (debug) {
          console.log('Arrays have different lengths:', x.length, y.length);
        }
        return false;
      }

      const sortedX = [...x].sort();
      const sortedY = [...y].sort();
      return sortedX.every((item, index) => isDeepEqual(item, sortedY[index], exceptionKeys, debug));
    }

    const x_keys = keys(x).filter((k: string) => exceptionKeys.indexOf(k) === -1);
    const y_keys = keys(y).filter((k: string) => exceptionKeys.indexOf(k) === -1);
    if (x_keys.length !== y_keys.length) {
      if (debug) {
        console.log('not equal key count:');
        console.log('keys(x)=', x_keys);
        console.log('keys(y)=', y_keys);
      }
      return false;
    }
    return x_keys.every(key => {
      const result = isDeepEqual(x[key], y[key], exceptionKeys, debug);
      if (debug && !result) {
        console.log('not equal key:', key);
      }
      return result;
    });
  }

  if ((x === undefined || x === null) && (y === undefined || y === null)) {
    return true;
  }

  if (debug && x !== y) {
    console.log('x=', x, 'y=', y)
  }
  return x === y;
}